import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';

// components
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';
import { BodyMedium, LinkSmall } from '@styled';

// functionality
import { CartItemMembershipContext } from './CartItemMembershipProvider';
import { LoadingContext } from '@context/LoadingProvider';
import { createCartItem, deleteCartItem } from '@general/api/cartAPI';
import { formatMoney } from '@general/helpers/currency.helpers';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useMembershipDuesYears from '@hooks/useCartDuesYears';
import useViewer from '@hooks/useViewer';

const DonationTitleStyled = styled(LinkSmall)`
    color: black !important;
    cursor: default;
    display: block;
`;

const CartItemMembershipDonation = ({ feesForType, type }) => {
    // -----------------------------
    // State
    // -----------------------------

    const { setIsLoading } = useContext(LoadingContext);
    const { membershipDataForMember, membershipCartItem } = useContext(CartItemMembershipContext);
    const [cartItem, setCartItem] = useState();
    const [applicableFee, setApplicableFee] = useState();

    // -----------------------------
    // Hooks
    // -----------------------------

    const { viewer } = useViewer();
    const { cart, refetch } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { oneOrThreeYears } = useMembershipDuesYears({
        memberId: membershipDataForMember.memberId,
        duesProductId: membershipCartItem.product_id,
    });

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (!membershipDataForMember || !applicableFee) {
            return;
        }

        setCartItem(membershipDataForMember.dues.find(dues => dues.product_id === applicableFee.id));
    }, [membershipDataForMember, applicableFee]);

    useEffect(() => {
        if (!feesForType || !oneOrThreeYears) {
            return;
        }

        setApplicableFee(feesForType.find(fee => fee.years === oneOrThreeYears));
    }, [feesForType, oneOrThreeYears]);

    // -----------------------------
    // Helpers
    // -----------------------------

    const getDescriptionText = () => {
        let builder = `Add a ${oneOrThreeYears}-Year `;

        if (type === 'sic') {
            builder += 'State Issued Contribution';
        } else if (type === 'pac') {
            builder += 'Political Action Committee Contribution';
        }

        return builder;
    };

    const addDonationToCart = async () => {
        setIsLoading(true);

        const cartItemData = [
            {
                member_id: membershipDataForMember.memberId,
                product_id: applicableFee.id,
                quantity: 1,
                meta_info: {
                    member_id: membershipCartItem.meta_info.member_id,
                    member_name: membershipCartItem.meta_info.member_name,
                    company_name: membershipCartItem.meta_info.company_name,
                    product_id: applicableFee.id,
                    product_code: applicableFee.product_code,
                    donation_description: applicableFee.description,
                    donation_years: applicableFee.years,
                },
            },
        ];

        if (type === 'pac') {
            cartItemData[0].meta_info.corporate = applicableFee.corporate;
        }

        const updatedCart = await createCartItem({
            cartId: cart.id,
            cartItemData,
            viewerId: viewer.recordId,
        });

        await refetch();

        setCartItem(updatedCart.items.find(registeredSession => registeredSession.product_id === applicableFee.id));
        setIsLoading(false);
    };

    const removeDonationFromCart = async () => {
        setIsLoading(true);

        await deleteCartItem({
            cartId: cart.id,
            cartItemId: cartItem.id,
            viewerId: viewer.recordId,
        });

        await refetch();

        setIsLoading(false);
    };

    // -----------------------------
    // Render
    // -----------------------------

    if (!cart || !applicableFee) {
        return null;
    }

    return (
        <div className="flex justify-between">
            <div>
                <CheckboxWithoutForm
                    checked={!!membershipDataForMember.dues.find(dues => dues.product_id === applicableFee.id)}
                    onCheckChange={checked => {
                        if (checked) {
                            addDonationToCart();
                        } else {
                            removeDonationFromCart();
                        }
                    }}
                >
                    <DonationTitleStyled as="span">
                        {getDescriptionText()}
                        {type === 'fnd' && (
                            <>
                                <>Donation to the </>
                                <a className="text-azure" href="/who-we-are/foundation" target="_blank">
                                    ICSC Foundation / ICSC Foundation Canada
                                </a>
                                <> for Talent Incubator Project & Student Programs</>
                            </>
                        )}
                    </DonationTitleStyled>
                </CheckboxWithoutForm>
            </div>

            <div className="ml-1.5rem">
                <BodyMedium>{formatMoney(applicableFee.price, cart.locale)}</BodyMedium>
            </div>
        </div>
    );
};

CartItemMembershipDonation.propTypes = {
    feesForType: PropTypes.arrayOf(
        PropTypes.shape({
            //
        })
    ),
    type: PropTypes.string,
};

export default CartItemMembershipDonation;
